import React, {
  JSX,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { darkTheme } from "@yleisradio/yds-core";
import styled, { css, ThemeProvider } from "styled-components";

import { LIVE_EDGE_THRESHOLD_IN_SECONDS } from "../../constants";
import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { UI_SIZE } from "../../enums";
import { isYleEmbed } from "../../helpers/commonUtils";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { controlsTextColor } from "../../styles/variables";
import { SettingsName } from "../../types/common";
import ReturnToYleDomain from "../ReturnToYleDomain";
import {
  AudioTracksOptions,
  AudioTracksTopLevelOption
} from "../settings/AudioTracks";
import {
  PlaybackRateOptions,
  PlaybackRateTopLevelOption
} from "../settings/PlaybackRate";
import { SettingsButton } from "../settings/SettingsButton";
import {
  SubtitlesOptions,
  SubtitlesTopLevelOption
} from "../settings/Subtitles";
import {
  SubtitlesBackgroundOptions,
  SubtitlesBackgroundTopLevelOption
} from "../settings/SubtitlesBackground";
import {
  SubtitlesSizeOptions,
  SubtitlesSizeTopLevelOption
} from "../settings/SubtitlesSize";
import {
  VideoTracksOptions,
  VideoTracksTopLevelOption
} from "../settings/VideoTracks";

import { handleKeyboardEvent } from "./helpers";

const SettingMenu = styled.div<{ isMobile?: boolean }>`
  align-items: flex-start;
  display: flex;
  flex-direction: column;

  > :not(:first-child) {
    margin-top: 16px;
  }
`;

const SettingsContainer = styled.div<{ isMobile: boolean }>`
  box-sizing: border-box;
  padding: 16px;
  pointer-events: auto;

  button,
  label,
  span {
    color: ${({ isMobile }) =>
      isMobile ? darkTheme.TEXT_NEGATIVE : controlsTextColor};
  }

  ${({ isMobile }) =>
    isMobile
      ? css`
          background: rgb(255 255 255 / 98%);
          border-radius: 8px;
          flex-shrink: 0;
          max-width: 100%;
          overflow-y: auto;
          position: relative;
          width: 290px;
        `
      : css`
          background: ${darkTheme.BACKGROUND_VARIANT};
          bottom: 0;
          border-radius: 8px 8px 0;
          overflow-y: auto;
          position: absolute;
          right: 20px;
          width: 300px;
        `}
`;

const SettingsBackground = styled.div<{
  isMobile: boolean;
}>`
  align-items: end;
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  padding: 10px;
  position: absolute;
  right: 0;
  z-index: 100;

  ${({ isMobile }) =>
    isMobile
      ? css`
          top: 0;
        `
      : css`
          bottom: 44px;
        `}
  &:before {
    content: "";
    display: block;
    flex-shrink: 1;
    height: 35px;
  }
`;

const mobileTheme = {
  inputBackgroundColor: "transparent",
  inputBorder: `2px solid ${darkTheme.BACKGROUND_INPUT}`
};

const desktopTheme = {
  inputBackgroundColor: darkTheme.BACKGROUND_NEGATIVE,
  inputBorder: "none"
};

enum MenuItems {
  TopLevel,
  AudioTracks,
  PlaybackRate,
  SubtitlesBackground,
  SubtilesSize,
  Subtitles,
  VideoTracks
}

const Settings = ({
  icon,
  settingsName
}: {
  icon: string;
  settingsName: SettingsName;
  // eslint-disable-next-line complexity
}) => {
  const { useStoreState } = usePlayerStore();
  const audioTracks = useStoreState.audioTracks();
  const device = useStoreState.device();
  const isAdaptiveBitrate = useStoreState.isAdaptiveBitrate();
  const isCasting = useStoreState.isCasting();
  const isLiveEdge = useStoreState.isLiveEdge();
  const playbackRate = useStoreState.playbackRate();
  const player = useStoreState.player();
  const playerElement = useStoreState.playerElement();
  const selectedVideoTrack = useStoreState.selectedVideoTrack();
  const subtitles = useStoreState.subtitles();
  const uiSize = useStoreState.uiSize();
  const uiSizeInPx = useStoreState.uiSizeInPx();
  const videoTracks = useStoreState.videoTracks();
  const visibleSettingsMenu = useStoreState.visibleSettingsMenu();

  const setVisibleSettingsMenu = useStoreState.setVisibleSettingsMenu();

  const [menuCssLeft, setMenuCssLeft] = useState("");
  const [menuCssMaxHeight, setMenuCssMaxHeight] = useState("none");
  const [menuItemVisible, setMenuItemVisible] = useState(MenuItems.TopLevel);
  const [abrLabel, setAbrLabel] = useState("");

  const playbackRateTimerRef = useRef<number>(undefined);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const settingsContainerRef = useRef<HTMLDivElement>(null);

  const { language, subtitles: subtitlesEnabled } = useEmbedParametersContext();

  const showSmallScreenSettings = device.isMobile || uiSize === UI_SIZE.XSMALL;

  useEffect(() => {
    if (playerElement && settingsButtonRef.current) {
      const observer = new IntersectionObserver(nodes => {
        observer.disconnect();

        const playerUiRect = nodes[0].boundingClientRect;
        const buttonRect = nodes[1].boundingClientRect;

        setMenuCssMaxHeight(
          playerUiRect.height - (showSmallScreenSettings ? 20 : 64) + "px"
        );

        if (!showSmallScreenSettings) {
          setMenuCssLeft(
            buttonRect.x + buttonRect.width - 300 - playerUiRect.x + "px"
          );
        }
      });

      observer.observe(playerElement);
      observer.observe(settingsButtonRef.current);

      return () => observer.disconnect();
    }
  }, [showSmallScreenSettings, playerElement, uiSizeInPx, visibleSettingsMenu]);

  useEffect(() => {
    const track =
      isAdaptiveBitrate &&
      videoTracks &&
      videoTracks.find(vt => vt.index === selectedVideoTrack.index);
    const suffix = track ? ` (${track.label})` : "";

    setAbrLabel(i18n("automatic", { language }) + suffix);
  }, [isAdaptiveBitrate, selectedVideoTrack, language, videoTracks]);

  useEffect(() => {
    setMenuItemVisible(MenuItems.TopLevel);
  }, [visibleSettingsMenu]);

  useEffect(() => {
    if (isLiveEdge && playbackRate > 1 && !playbackRateTimerRef.current) {
      playbackRateTimerRef.current = setTimeout(
        () => {
          player.setPlaybackRate(1);
          playbackRateTimerRef.current = undefined;
        },
        (LIVE_EDGE_THRESHOLD_IN_SECONDS * 1000) / playbackRate
      ) as unknown as number;
    }
  }, [isLiveEdge, playbackRate, player]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent | React.KeyboardEvent) => {
      if (event.key === "Escape") {
        setVisibleSettingsMenu(undefined);
      }
    },
    [setVisibleSettingsMenu]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(playbackRateTimerRef.current);
    };
  }, [onKeyDown, setVisibleSettingsMenu]);

  const onButtonBlur = event => {
    if (
      settingsButtonRef.current &&
      settingsContainerRef.current &&
      !settingsButtonRef.current.contains(event.relatedTarget) &&
      !settingsContainerRef.current.contains(event.relatedTarget)
    ) {
      setVisibleSettingsMenu(undefined);
    }
  };

  const SettingMenuTopLevelButtonSubtitles = subtitlesEnabled &&
    subtitles.length > 0 && (
      <SubtitlesTopLevelOption
        autoFocus={true}
        key="1"
        onBlur={onButtonBlur}
        onClick={() => setMenuItemVisible(MenuItems.Subtitles)}
      />
    );

  const SettingMenuTopLevelButtonSubtitlesSize = subtitlesEnabled &&
    subtitles.length > 0 && (
      <SubtitlesSizeTopLevelOption
        key="2"
        onBlur={onButtonBlur}
        onClick={() => {
          setMenuItemVisible(MenuItems.SubtilesSize);
        }}
      />
    );

  const SettingMenuTopLevelButtonAudioTracks = audioTracks &&
    audioTracks.length > 1 && (
      <AudioTracksTopLevelOption
        key="3"
        onBlur={onButtonBlur}
        onClick={() => {
          setMenuItemVisible(MenuItems.AudioTracks);
        }}
      />
    );

  const SettingMenuTopLevelButtonPlaybackRate = (
    <PlaybackRateTopLevelOption
      autoFocus={true}
      key="4"
      onBlur={onButtonBlur}
      onClick={() => {
        setMenuItemVisible(MenuItems.PlaybackRate);
      }}
    />
  );

  const SettingMenuTopLevelButtonVideoTracks = videoTracks &&
    videoTracks.length > 1 && (
      <VideoTracksTopLevelOption
        abrLabel={abrLabel}
        key="5"
        onBlur={onButtonBlur}
        onClick={() => {
          setMenuItemVisible(MenuItems.VideoTracks);
        }}
      />
    );

  const SettingMenuTopLevelButtonSubtitlesBackground = subtitlesEnabled &&
    subtitles.length > 0 && (
      <SubtitlesBackgroundTopLevelOption
        key="6"
        onBlur={onButtonBlur}
        onClick={() => {
          setMenuItemVisible(MenuItems.SubtitlesBackground);
        }}
      />
    );

  const SettingMenuTopLevelButtonAreenaLink = !isYleEmbed() ? (
    <ReturnToYleDomain />
  ) : (
    false
  );

  const topLevelMenuButtons: (JSX.Element | false | undefined)[] = [];

  switch (settingsName) {
    case "desktop-texts":
      topLevelMenuButtons.push(SettingMenuTopLevelButtonAudioTracks);
      topLevelMenuButtons.push(SettingMenuTopLevelButtonSubtitles);
      topLevelMenuButtons.push(SettingMenuTopLevelButtonSubtitlesBackground);
      topLevelMenuButtons.push(SettingMenuTopLevelButtonSubtitlesSize);
      break;
    case "desktop-settings":
      if (!isCasting) {
        topLevelMenuButtons.push(SettingMenuTopLevelButtonPlaybackRate);
        topLevelMenuButtons.push(SettingMenuTopLevelButtonVideoTracks);
      }
      break;
    case "mobile":
      topLevelMenuButtons.push(SettingMenuTopLevelButtonAreenaLink);
      topLevelMenuButtons.push(SettingMenuTopLevelButtonAudioTracks);
      topLevelMenuButtons.push(SettingMenuTopLevelButtonSubtitles);
      topLevelMenuButtons.push(SettingMenuTopLevelButtonSubtitlesSize);
      topLevelMenuButtons.push(SettingMenuTopLevelButtonSubtitlesBackground);
      if (!isCasting) {
        topLevelMenuButtons.push(SettingMenuTopLevelButtonPlaybackRate);
        topLevelMenuButtons.push(SettingMenuTopLevelButtonVideoTracks);
      }
      break;
  }

  const backToTopLevel = () => {
    setMenuItemVisible(MenuItems.TopLevel);
  };

  const SettingsListing = (
    <>
      {menuItemVisible === MenuItems.TopLevel && (
        <SettingMenu role="menu">{topLevelMenuButtons}</SettingMenu>
      )}
      {menuItemVisible === MenuItems.Subtitles && (
        <SubtitlesOptions onBlur={onButtonBlur} onClickBack={backToTopLevel} />
      )}
      {menuItemVisible === MenuItems.SubtilesSize && (
        <SubtitlesSizeOptions
          onBlur={onButtonBlur}
          onClickBack={backToTopLevel}
        />
      )}
      {menuItemVisible === MenuItems.SubtitlesBackground && (
        <SubtitlesBackgroundOptions
          onBlur={onButtonBlur}
          onClickBack={backToTopLevel}
        />
      )}
      {menuItemVisible === MenuItems.AudioTracks && (
        <AudioTracksOptions
          onBlur={onButtonBlur}
          onClickBack={backToTopLevel}
        />
      )}
      {menuItemVisible === MenuItems.PlaybackRate && (
        <PlaybackRateOptions
          onBlur={onButtonBlur}
          onClickBack={backToTopLevel}
        />
      )}
      {menuItemVisible === MenuItems.VideoTracks && (
        <VideoTracksOptions
          onBlur={onButtonBlur}
          abrLabel={abrLabel}
          onClickBack={backToTopLevel}
        />
      )}
    </>
  );

  const isMenuEmpty = topLevelMenuButtons.filter(t => t).length === 0;

  return !isMenuEmpty ? (
    <div ref={settingsRef} onKeyDown={onKeyDown}>
      <SettingsButton
        icon={icon}
        settingsName={settingsName}
        ref={settingsButtonRef}
      />
      {visibleSettingsMenu === settingsName && (
        <SettingsBackground
          isMobile={showSmallScreenSettings}
          style={{ height: menuCssMaxHeight }}>
          <SettingsContainer
            isMobile={showSmallScreenSettings}
            ref={settingsContainerRef}
            onKeyDown={event => {
              if (event.key !== "Escape") {
                event.stopPropagation();
              } else {
                settingsButtonRef.current?.focus();
              }
            }}
            onKeyUp={handleKeyboardEvent}
            style={{
              left: menuCssLeft,
              maxHeight: menuCssMaxHeight
            }}
            tabIndex={-1}>
            <ThemeProvider
              theme={showSmallScreenSettings ? mobileTheme : desktopTheme}>
              {SettingsListing}
            </ThemeProvider>
          </SettingsContainer>
        </SettingsBackground>
      )}
    </div>
  ) : (
    <></>
  );
};

export default memo(Settings);
